/**
 * LifecycleReadonlySidebarController — désactivation visuelle des
 * sections de navigation pour les comptes en `archived_readonly` §25.F
 * (EPIC-037 / 37.6).
 *
 * Monté sur l'`<aside class="nm-sidebar nm-app__sidebar">` du layout
 * `app`. Lit `getLifecycle()` au connect() :
 *   - si `lifecycleStatus !== 'archived_readonly'` → no-op (la sidebar
 *     reste pleinement opérationnelle, même en `resiliation_pending` —
 *     seul le bandeau permanent du `ResignationBannerController`
 *     s'affiche dans ce dernier cas).
 *   - sinon → grise les sections de saisie / pilotage / déclarations,
 *     le bouton « Créer » du topbar et les entrées profil de l'avatar.
 *     Les sections lecture seule (Tableau de bord, Factures émises,
 *     Factures reçues, Coffre-fort, Dossier public, Registres,
 *     Paramètres, Abonnement) restent accessibles.
 *
 * Best-effort : si l'appel `getLifecycle()` échoue (401, réseau), on
 * laisse la sidebar telle quelle — l'utilisateur n'est pas pénalisé
 * par un grisage côté frontend sur un état inconnu.
 *
 * Liste des sélecteurs grisés (cf. maquette `archive-readonly.html`) :
 *   - `a[href="/devis.html"]` (sidebar — Devis)
 *   - `a[href="/pilotage.html"]` (sidebar — Pilotage)
 *   - `a[href="/declarations.html"]` (sidebar — Déclarations)
 *   - `.nm-topbar__action--create` (topbar — bouton +Créer)
 *   - `a[href="/profil-utilisateur.html"]` (avatar menu)
 *   - `a[href="/profil-entreprise.html"]` (avatar menu)
 */

import BaseController from "./base_controller.js";
import { api } from "/js/services/api.js";

const DISABLED_TOOLTIP = "Indisponible — compte résilié, données en lecture seule";

const DISABLED_LINK_SELECTORS = [
  'a[href="/devis.html"]',
  'a[href="/pilotage.html"]',
  'a[href="/declarations.html"]',
  'a[href="/profil-utilisateur.html"]',
  'a[href="/profil-entreprise.html"]',
];

const DISABLED_BUTTON_SELECTORS = [
  ".nm-topbar__action--create",
];

export default class LifecycleReadonlySidebarController extends BaseController {
  async connect() {
    let state;
    try {
      state = await api.getLifecycle();
    } catch {
      return;
    }
    if (state?.lifecycleStatus !== "archived_readonly") {
      return;
    }
    this._applyReadonlyTreatment();
  }

  _applyReadonlyTreatment() {
    const root = document;
    for (const selector of DISABLED_LINK_SELECTORS) {
      for (const node of root.querySelectorAll(selector)) {
        this._disableLink(node);
      }
    }
    for (const selector of DISABLED_BUTTON_SELECTORS) {
      for (const node of root.querySelectorAll(selector)) {
        this._disableButton(node);
      }
    }
  }

  _disableLink(node) {
    node.setAttribute("aria-disabled", "true");
    node.setAttribute("tabindex", "-1");
    node.setAttribute("title", DISABLED_TOOLTIP);
    node.style.opacity = "0.45";
    node.style.pointerEvents = "none";
    node.style.cursor = "not-allowed";
  }

  _disableButton(node) {
    node.setAttribute("disabled", "");
    node.setAttribute("aria-disabled", "true");
    node.setAttribute("title", DISABLED_TOOLTIP);
    node.style.opacity = "0.45";
    node.style.cursor = "not-allowed";
  }
}
